import type { Page, Pageable } from '../../../core/api/spring-types';
import { normalizePage } from '../../../core/api/spring-types';
import type {
  WasteCollectionFormDto,
  WasteCollectionFormReportDto
} from '../../../api/generated/types.gen';
import {
    getAllWasteCollectionForms,
    getWasteCollectionFormById,
    getDefaultWasteCollectionForm,
    createWasteCollectionForm,
    updateWasteCollectionForm,
    updateWasteCollectionFormByAdmin,
    deleteWasteCollectionForm,
    getWasteCollectionFormsReport,
    validateWasteCollectionForm,
    updateWasteCollectionFormRealValues,
    processWasteCollectionForm,
} from '../../../api/generated/sdk.gen';
import { handleResponse } from '@/core/api/response-handler';

// Export DTO types for use in other modules
export type WasteCollectionFormDTO = WasteCollectionFormDto;
export type WasteCollectionFormReportDTO = WasteCollectionFormReportDto;

/**
 * Waste Collection API - Thin wrapper for auto-generated SDK functions
 * Maintains backward compatibility with existing hook signatures
 */
class WasteCollectionAPI {
    /**
     * Get waste orders report with pagination, filtering, and sorting
     * Endpoint: GET /api/waste-collection-forms/report
     * Note: WasteCollectionFormReportDto serves both as filter params and response type (backend pattern)
     */
    async getReport(
        filter: any,
        pageable: Pageable
    ): Promise<Page<any>> {
        const response = await getWasteCollectionFormsReport({
            query: { ...filter, ...pageable } as any,
        });
        return normalizePage(handleResponse(response));
    }

    /**
     * Get all waste collection forms (no pagination)
     * Endpoint: GET /api/waste-collection-forms
     */
    async getAll(): Promise<WasteCollectionFormDto[]> {
        const response = await getAllWasteCollectionForms();
        return handleResponse(response);
    }

    /**
     * Get waste collection form by ID
     * Endpoint: GET /api/waste-collection-forms/{id}
     */
    async getById(id: string): Promise<WasteCollectionFormDto> {
        const response = await getWasteCollectionFormById({
            path: { id },
        });
        return handleResponse(response);
    }

    /**
     * Get default waste collection form template
     * Endpoint: GET /api/waste-collection-forms/default
     */
    async getDefault(): Promise<WasteCollectionFormDto> {
        const response = await getDefaultWasteCollectionForm();
        return handleResponse(response);
    }

    /**
     * Create new waste collection form
     * Endpoint: POST /api/waste-collection-forms
     */
    async create(data: WasteCollectionFormDto): Promise<WasteCollectionFormDto> {
        const response = await createWasteCollectionForm({
            body: data,
        });
        return handleResponse(response);
    }

    /**
     * Update waste collection form
     * Endpoint: PATCH /api/waste-collection-forms
     */
    async update(data: WasteCollectionFormDto): Promise<WasteCollectionFormDto> {
        const response = await updateWasteCollectionForm({
            body: data,
        });
        return handleResponse(response);
    }

    /**
     * Admin update waste collection form
     * Endpoint: PATCH /api/waste-collection-forms/admin-update
     */
    async adminUpdate(data: WasteCollectionFormDto): Promise<WasteCollectionFormDto> {
        const response = await updateWasteCollectionFormByAdmin({
            body: data,
        });
        return handleResponse(response);
    }

    /**
     * Update order validation status (approve/reject)
     * Endpoint: PATCH /api/waste-collection-forms/{id}/validation
     */
    async updateValidation(id: string, params: {
        isValidated?: boolean;
        rejectReason?: string;
    }): Promise<void> {
        const response = await validateWasteCollectionForm({
            path: { id },
            query: params as any,
        });
        handleResponse(response);
    }

    /**
     * Update logistics data (real waste values and documents)
     * Endpoint: PATCH /api/waste-collection-forms/{id}/waste-real-values
     */
    async updateLogistics(id: string, data: {
        realDeeeKg?: number;
        realDbaKg?: number;
        realBecuriSiNeoaneKg?: number;
        proceseVerbale?: string[];
    }): Promise<void> {
        const response = await updateWasteCollectionFormRealValues({
            path: { id },
            query: data as any,
        });
        handleResponse(response);
    }

    /**
     * Mark order as processed/transmitted
     * Endpoint: PATCH /api/waste-collection-forms/{id}/process
     */
    async markProcessed(id: string): Promise<void> {
        const response = await processWasteCollectionForm({
            path: { id },
            query: { isProcessed: true },
        });
        handleResponse(response);
    }

    /**
     * Delete waste collection form
     * Endpoint: DELETE /api/waste-collection-forms/{id}
     */
    async delete(id: string): Promise<void> {
        const response = await deleteWasteCollectionForm({
            path: { id },
        });
        handleResponse(response);
    }
}

export const wasteCollectionApi = new WasteCollectionAPI();
